import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import Icon from '../../icon'
import { formatLocation } from '../../../lib/address'
import styles from './UserProfileLeftSidebar.module.scss'

/** REVISIT: this is the format event date for display, do we have a hook for this I forgot to check */
function formatEventDate(dateStr, locale = 'en') {
  if (!dateStr) return ''
  const m = moment(dateStr).tz(moment.tz.guess())
  m.locale(locale === 'es' ? 'es' : 'en')
  return m.format('MMMM D, YYYY')
}

/**
 * User profile left sidebar (3 col). First block: name, Community/location, quote, followers.
 * Then Supports (nonprofit + causes), Calendar, Awards, Groups. Uses shared section title row + CR colors.
 */
export default function UserProfileLeftSidebar({
  name,
  location,
  quote,
  quoteAuthor,
  followersCount,
  onFollowersClick,
  onEditCommunity,
  favoriteCharity,
  categories = [],
  groups = [],
  upcomingEvents = [],
  awards = [],
  locale = 'en',
}) {
  const { t } = useTranslation()

  const supportsItems = []
  if (favoriteCharity?.organization_name) {
    supportsItems.push({
      type: 'nonprofit',
      labelKey: 'profiles.nonprofit',
      name: favoriteCharity.organization_name,
      icon: 'landmark',
    })
  }
  const causeCategories = Array.isArray(categories) ? categories : []
  causeCategories.forEach(cat => {
    const displayName =
      locale === 'es' ? cat.es_name ?? cat.en_name : cat.en_name ?? cat.es_name
    if (displayName) {
      supportsItems.push({
        type: 'cause',
        labelKey: 'profiles.cause',
        name: displayName,
        icon: cat.icon || 'heart',
      })
    }
  })

  return (
    <div className={styles.sidebar}>
      {/* first block: name, Community/location, quote, followers (per design) */}
      <section className={styles.section}>
        <div className={styles.nameRow}>
          {name != null && <h2 className={styles.name}>{name}</h2>}
          <Icon name="circle-user" className={styles.nameIcon} aria-hidden />
        </div>

        <hr className={styles.divider} />

        <div className={styles.communityBlock}>
          <div className={styles.communityLabelRow}>
            <span className={styles.communityLabel}>
              {t('profiles.community', 'Community')}
            </span>
            {onEditCommunity && (
              <button
                type="button"
                className={styles.editIconBtn}
                onClick={onEditCommunity}
                aria-label={t('profiles.editCommunity', 'Edit community')}
              >
                <Icon name="edit" className={styles.editIcon} />
              </button>
            )}
          </div>
          {location != null && location !== '' && (
            <p className={styles.location}>{location}</p>
          )}
        </div>

        {quote != null && quote !== '' && (
          <>
            <p className={styles.quote}>{quote}</p>
            {quoteAuthor != null && quoteAuthor !== '' && (
              <p className={styles.quoteAuthor}>~ {quoteAuthor}</p>
            )}
          </>
        )}

        <hr className={styles.divider} />

        {typeof onFollowersClick === 'function' ? (
          <button
            type="button"
            className={styles.followersRowButton}
            onClick={onFollowersClick}
            aria-label={t('profiles.viewFollowers', 'View followers list')}
          >
            <div className={styles.followersRow}>
              <Icon
                name="user-plus"
                className={styles.followersIcon}
                aria-hidden
              />
              <span className={styles.followersText}>
                <span className={styles.followersCount}>
                  {followersCount ?? 0}
                </span>{' '}
                {t('profiles.followers', 'Followers')}
              </span>
            </div>
          </button>
        ) : (
          <div className={styles.followersRow}>
            <Icon
              name="user-plus"
              className={styles.followersIcon}
              aria-hidden
            />
            <span className={styles.followersText}>
              <span className={styles.followersCount}>
                {followersCount ?? 0}
              </span>{' '}
              {t('profiles.followers', 'Followers')}
            </span>
          </div>
        )}
      </section>

      {/* supports: favorite nonprofit + causes */}
      <section className={styles.section}>
        <div className={styles.sectionTitleRow}>
          <h3 className={styles.sectionTitle}>
            {t('profiles.supports', 'Supports')}
          </h3>
          <Icon
            name="hands-holding-heart"
            className={styles.sectionTitleIcon}
            aria-hidden
          />
        </div>
        <hr className={styles.divider} />
        {supportsItems.length > 0 ? (
          <ul className={styles.supportList}>
            {supportsItems.map((item, i) => (
              <li key={`${item.type}-${i}`} className={styles.supportItem}>
                <Icon
                  name={item.icon}
                  className={styles.supportIcon}
                  aria-hidden
                />
                <div className={styles.supportInfo}>
                  <span className={styles.supportLabel}>
                    {t(item.labelKey, item.type)}
                  </span>
                  <span className={styles.supportName}>{item.name}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.placeholder}>
            {t('profiles.supportsEmpty', 'No supports selected yet.')}
          </p>
        )}
      </section>

      {/* calendar: associated events */}
      <section className={styles.section}>
        <div className={styles.sectionTitleRow}>
          <h3 className={styles.sectionTitle}>
            {t('profiles.calendar', 'Calendar')}
          </h3>
          <Icon
            name="calendar-days"
            className={styles.sectionTitleIcon}
            aria-hidden
          />
        </div>
        <hr className={styles.divider} />
        {Array.isArray(upcomingEvents) && upcomingEvents.length > 0 ? (
          <ul className={styles.eventList}>
            {upcomingEvents.slice(0, 5).map((event, i) => {
              const thumbUrl =
                event.media_attachments?.[0]?.resized_image_url ??
                event.media_attachments?.[0]?.direct_upload_url ??
                event.media_attachments?.[0]?.thumbnail_url
              const eventType = event.categories?.[0]
                ? locale === 'es'
                  ? event.categories[0].es_name
                  : event.categories[0].en_name
                : t('profiles.volunteering', 'volunteering')
              const locationStr =
                formatLocation(event?.location) ||
                event?.location?.google_description ||
                ''
              const eventSlug = event.slug ?? event.id
              return (
                <li key={event.id ?? i} className={styles.eventItem}>
                  <Link
                    to={`/events/${eventSlug}`}
                    className={styles.eventLink}
                  >
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt=""
                        className={styles.eventThumb}
                      />
                    ) : (
                      <span
                        className={styles.eventThumbPlaceholder}
                        aria-hidden
                      />
                    )}
                    <div className={styles.eventContent}>
                      <div className={styles.eventType}>{eventType}</div>
                      <div className={styles.eventName}>
                        {event.name ?? event.title}
                      </div>
                      {locationStr && (
                        <div className={styles.eventLocation}>
                          {locationStr}
                        </div>
                      )}
                      {event.event_start_date && (
                        <div className={styles.eventDate}>
                          {formatEventDate(event.event_start_date, locale)}
                        </div>
                      )}
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className={styles.placeholder}>
            {t('profiles.calendarEmpty', 'No upcoming events.')}
          </p>
        )}
      </section>

      {/* awards */}
      <section className={styles.section}>
        <div className={styles.sectionTitleRow}>
          <h3 className={styles.sectionTitle}>
            {t('profiles.awards', 'Awards')}
          </h3>
          <Icon name="trophy" className={styles.sectionTitleIcon} aria-hidden />
        </div>
        <hr className={styles.divider} />
        {Array.isArray(awards) && awards.length > 0 ? (
          <ul className={styles.supportList}>
            {awards.map((award, i) => (
              <li key={award.id ?? i} className={styles.supportItem}>
                <Icon name="award" className={styles.supportIcon} aria-hidden />
                <span className={styles.supportName}>
                  {award.name ?? award.title}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.awardsEmpty}>
            <Icon
              name="trophy"
              className={styles.awardsEmptyIcon}
              aria-hidden
            />
            <span className={styles.awardsEmptyText}>
              {t('profiles.awardsNoneYet', 'none yet!')}
            </span>
          </div>
        )}
      </section>

      {/* groups */}
      <section className={styles.section}>
        <div className={styles.sectionTitleRow}>
          <h3 className={styles.sectionTitle}>
            {t('profiles.groups', 'Groups')}
          </h3>
          <Icon name="users" className={styles.sectionTitleIcon} aria-hidden />
        </div>
        <hr className={styles.divider} />
        {Array.isArray(groups) && groups.length > 0 ? (
          <ul className={styles.groupList}>
            {groups.slice(0, 5).map((group, i) => {
              const groupSlug = group.slug ?? group.id
              return (
                <li key={group.id ?? i} className={styles.groupItem}>
                  <Link
                    to={`/groups/${groupSlug}`}
                    className={styles.groupLink}
                  >
                    {group.image_url ? (
                      <img
                        src={group.image_url}
                        alt=""
                        className={styles.groupImage}
                      />
                    ) : (
                      <span
                        className={styles.groupImagePlaceholder}
                        aria-hidden
                      />
                    )}
                    <div className={styles.groupInfo}>
                      <span className={styles.groupMeta}>
                        {t(
                          'profiles.membersCount',
                          {
                            count: group.user_count ?? group.members_count ?? 0,
                          },
                          '{{count}} members',
                        )}
                      </span>
                      <span className={styles.groupName}>{group.name}</span>
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className={styles.placeholder}>
            {t('profiles.groupsEmpty', 'No groups yet.')}
          </p>
        )}
      </section>
    </div>
  )
}

UserProfileLeftSidebar.propTypes = {
  name: PropTypes.string,
  location: PropTypes.string,
  quote: PropTypes.string,
  quoteAuthor: PropTypes.string,
  followersCount: PropTypes.number,
  onFollowersClick: PropTypes.func,
  onEditCommunity: PropTypes.func,
  favoriteCharity: PropTypes.shape({
    organization_name: PropTypes.string,
  }),
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      en_name: PropTypes.string,
      es_name: PropTypes.string,
      icon: PropTypes.string,
    }),
  ),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      slug: PropTypes.string,
      name: PropTypes.string,
      image_url: PropTypes.string,
      user_count: PropTypes.number,
    }),
  ),
  upcomingEvents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      slug: PropTypes.string,
      name: PropTypes.string,
      title: PropTypes.string,
      event_start_date: PropTypes.string,
      location: PropTypes.object,
      media_attachments: PropTypes.array,
      categories: PropTypes.array,
    }),
  ),
  awards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  locale: PropTypes.string,
}
